<script lang="ts">
import type { TooltipContentEmits, TooltipContentProps, TooltipRootEmits, TooltipRootProps } from 'reka-ui'
import type { EmitsToProps } from '../types/utils'

export interface TooltipProps extends TooltipRootProps {
  text?: string
  content?: Omit<TooltipContentProps, 'as' | 'asChild'> & Partial<EmitsToProps<TooltipContentEmits>>
  portal?: boolean | string | HTMLElement
  delayDuration?: number
  maxWidth?: number
  class?: string | object
  variant?: 'dark' | 'white'
}

export interface TooltipEmits extends TooltipRootEmits {}

export interface TooltipSlots {
  default?: (props: { open: boolean }) => any
  content?: (props?: object) => any
}

const ui = {
  content: {
    dark: 'flex items-center px-3 py-1.5 bg-secondary-900 shadow-sm rounded-lg text-md text-white',
    white: 'flex items-center px-3 py-1.5 bg-white shadow-sm rounded-lg text-md text-secondary-900 border border-secondary-200',
  },
  text: '',
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import defu from 'defu'
import { TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger, useForwardPropsEmits } from 'reka-ui'
import { toRef } from 'vue'
import { usePortal } from '../composables/usePortal'

const props = withDefaults(defineProps<TooltipProps>(), {
  portal: true,
  delayDuration: 300,
  variant: 'dark',
})
const emits = defineEmits<TooltipEmits>()
const slots = defineSlots<TooltipSlots>()

const rootProps = useForwardPropsEmits(reactivePick(props, 'defaultOpen', 'open', 'delayDuration', 'disableHoverableContent', 'disableClosingTrigger', 'disabled', 'ignoreNonKeyboardFocus'), emits)
const portalProps = usePortal(toRef(() => props.portal))
const contentProps = toRef(() => defu(props.content, { side: 'top', sideOffset: 8, collisionPadding: 8 }) as TooltipContentProps)
</script>

<template>
  <TooltipProvider>
    <TooltipRoot v-slot="{ open }" v-bind="rootProps">
      <TooltipTrigger v-if="!!slots.default" v-bind="$attrs" as-child :class="props.class">
        <slot :open="open" />
      </TooltipTrigger>

      <TooltipPortal v-bind="portalProps">
        <TooltipContent v-bind="contentProps" :class="ui.content[props.variant]" :style="{ maxWidth: `${props.maxWidth ?? 300}px`, whiteSpace: 'normal', wordWrap: 'break-word', wordBreak: 'break-word', zIndex: 9999 }">
          <slot name="content">
            <span v-if="text" :class="ui.text">{{ text }}</span>
          </slot>
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>
